import { createPinia } from 'pinia';
import { Component, createApp } from 'vue';

import { userCookies } from '@/shared/constants/constants';
import { profileQuery } from '@/shared/graphql/gateway/community/queries';
import { parseConsentCookie } from '@/shared/pinia/modules/posthog/consent';
import { usePostHogStore } from '@/shared/pinia/modules/posthog/posthog.store';
import { getCookie } from '@/shared/utils/cookie';
import { getMarketCountryCode } from '@/shared/utils/country.js';
import {
  isForeignPass,
  isPassExpired,
  isPassFresh,
  parseJwt,
  passExpiresAt
} from '@/shared/utils/jwt';
import { hobbiiLocalStorage } from '@/shared/utils/web-storage';

import { useCartStore } from '../pinia/modules/cart';
import { useConfigStore } from '../pinia/modules/config';
import { useUserStore } from '../pinia/modules/user';
import { useWishlistStore } from '../pinia/modules/wishlist';
import { apolloClientInit } from '../utils/apollo-client';
import { getToken } from '../utils/get-token';
import { klaviyoIdentify } from '../utils/tracking';

// When `email` is undefined Liquid named no customer — the JWT is then the
// only identity anchor available. Failing open here is intentional because the
// subsequent signature and expiry checks remain the guards.
const passBelongsTo = (token: string, email?: string) => {
  if (!email) {
    return true;
  }

  const claimed = parseJwt(token)?.email;

  return (
    typeof claimed === 'string' && claimed.toLowerCase() === email.toLowerCase()
  );
};

export const initHobbiiStarter = async () => {
  const alreadyInitialized = !!window.hobbiiStore;

  if (!alreadyInitialized) {
    window.hobbiiStore = createPinia();

    // Set active Pinia instance.
    // Pinia needs access to an app to be available everywhere.
    createApp(null as unknown as Component).use(window.hobbiiStore);

    const graphqlHost = import.meta.env.VITE_HOBBII_GATEWAY;
    const apolloClient = apolloClientInit({ graphqlHost });

    // TODO: Get rid of this plugin approach, it hides circular deps & using `this` in JS is a mine field.
    window.hobbiiStore.use(() => ({ apolloClient })); // Apollo Client plugin

    const configStore = useConfigStore();
    const userStore = useUserStore();
    if (window.Hobbii?.hobbiiStoreData) {
      const { countryCode, language, isLogged, pointsMultiplier } =
        window.Hobbii.hobbiiStoreData;

      configStore.$patch({
        countryCode,
        language,
        graphqlHost,
        pointsMultiplier
      });

      // The buyer can hold a valid managed-login (c_id) token even when
      // Shopify's Liquid `customer` object — and thus `isLogged` — is not
      // populated. Boot the session from that token as well, so the account app
      // loads reliably after the move to hosted customer accounts.
      // Sort by expiry descending so the pass with the longest remaining
      // lifetime wins, regardless of whether it came from the cookie or
      // localStorage. The cookie is written once at login and never refreshed,
      // so localStorage can hold a newer pass after a silent token rotation.
      const heldPasses = [
        getCookie(userCookies.C_ID),
        hobbiiLocalStorage.getItem(userCookies.C_ID)
      ]
        .filter((pass): pass is string => !!pass && !isForeignPass(pass))
        .sort((a, b) => (passExpiresAt(b) ?? 0) - (passExpiresAt(a) ?? 0));

      const liquidEmail = window.Hobbii.hobbiiStoreData.customer?.email;
      const heldPassWhere = (usable: (pass: string) => boolean) =>
        heldPasses.find(
          (pass) => usable(pass) && passBelongsTo(pass, liquidEmail)
        );

      if (isLogged || heldPasses.length > 0) {
        // A pass with time left on it avoids asking Shopify at all, which is
        // the only moment an empty `logged_in_customer_id` can sign a
        // genuinely signed-in customer out.
        const freshPass = heldPassWhere(isPassFresh);

        if (freshPass) {
          userStore.setCustomer(freshPass);
        } else {
          let token = await getToken();
          if (!token) {
            token = await getToken(true);
          }

          const unexpiredPass = heldPassWhere((pass) => !isPassExpired(pass));

          if (token) {
            userStore.setCustomer(token);
          } else if (unexpiredPass) {
            // Refresh failed on a pass that is not expired: keep the session
            // rather than let a transient Shopify failure end it.
            userStore.setCustomer(unexpiredPass);
          } else {
            // Fails closed: an expired pass must not boot a session, however
            // intact its claims look (TAF-3375).
            userStore.cleanCustomer();
          }
        }

        if (userStore.customer?.uuid) {
          try {
            const { data } = await apolloClient.query({
              query: profileQuery,
              variables: {
                userId: userStore.customer.uuid
              }
            });

            if (data?.community?.profile?.user) {
              userStore.setCommunityProfile(data.community.profile.user);
            }
          } catch (err) {
            console.error('Failed to get starter data', err);
          }
        }

        if (userStore.customer?.email) {
          klaviyoIdentify({
            email: userStore.customer.email,
            firstName: userStore.customer.first_name || undefined
          });
        }
      } else {
        userStore.cleanCustomer();
      }
    }

    if (window.Hobbii) {
      window.Hobbii.storeDefaultCountryCode = getMarketCountryCode();
    }

    const cartStore = useCartStore();
    cartStore.getCart();

    if (userStore.isLogged) {
      const wishlistStore = useWishlistStore();
      wishlistStore.loadWishlistItems();
    }

    const posthogStore = usePostHogStore();
    posthogStore.initialize();

    window.addEventListener('ucEvent', (event) => {
      const e = event as CustomEvent<{ event?: string }>;
      if (e.detail?.event !== 'consent_status') return;

      void posthogStore.updateConsent(parseConsentCookie());
    });
  }
};
